import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Card, { CardNames } from '../../../components/Card/Card.tsx';
import Header from '../Header/Header.tsx';
import {
  claimReservation,
  getUserCredits,
  getReservationConfirmation,
  claimReservationForUnclaimedReservation,
} from '../../../lib/api.ts';
import { ApiError } from '../../../lib/ApiError.ts';
import type { ActionRequiredResponse } from '../../../lib/types.ts';
import RemainingTime from '../RemainingTime/RemainingTime.tsx';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  AuthAtom,
  PaymentMethodsByIdAtom,
  ReservationAtom,
  useDirectLinkState,
} from '../lib/state.ts';

import styles from './Confirm.module.css';
import Config from '@/lib/Config.ts';
import dayjs from 'dayjs';
import { useSetAtom } from 'jotai/index';

// Fallback copy for claim failures this flow can't classify [HOS-2029]
const GENERIC_CLAIM_ERROR = 'Something went wrong completing your booking. Please try again.';

const Confirm: React.FC = () => {
  const navigate = useNavigate();
  const [booking, setBooking] = useState<boolean>(false);
  // Synchronous double-click guard. State updates are batched, so the next
  // click's handler can still see the prior `booking === false`. Refs update
  // synchronously and don't suffer the closure-staleness problem — same
  // pattern as `inFlightRef` in the native EventBooking / AcceptBooking
  // screens. The `booking` state still drives UI; the ref is the gate.
  const inFlightRef = useRef(false);
  const auth = useAtomValue(AuthAtom);
  const reservation = useAtomValue(ReservationAtom);
  const setReservation = useSetAtom(ReservationAtom);
  const [directLink, updateDirectLink] = useDirectLinkState();
  const methods_by_id = useAtomValue(PaymentMethodsByIdAtom);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [creditsAvailable, setCreditsAvailable] = useState<boolean>(false);
  const [useCredits, setUseCredits] = useState<boolean>(false);

  useEffect(() => {
    const waitForIt = async () => {
      setStripe(await loadStripe(Config.data.stripe.key));
    };
    void waitForIt();

    if (reservation?.claimed_at && !reservation?.confirmed_at) {
      navigate(`../processing_payment/`);
      return;
    }

    // Guard: redirect to payment step if no valid payment method is saved
    // and the reservation requires payment (total_paid > 0)
    if (reservation && reservation.total_paid > 0 && !directLink.claim_body?.payment_method_id) {
      navigate(`../payment/`);
      return;
    }

    void onCheckCredits();
  }, [reservation]);

  const onCheckCredits = async () => {
    let user_credits_response;
    if (auth?.token) {
      user_credits_response = await getUserCredits(auth!.token);
    }
    if (!user_credits_response) {
      return;
    }
    const hasCredits = user_credits_response.data.credits.USD !== '$0.00';
    const hasPoints = user_credits_response.data.points > 0;

    setCreditsAvailable(hasCredits || hasPoints);

    if (
      (hasCredits || hasPoints) &&
      (reservation!.credits_used || reservation!.points_used) &&
      auth
    ) {
      setUseCredits(true);
    }
  };

  const onConfirm = async (useCreditsValue: boolean) => {
    setUseCredits(useCreditsValue);

    const response = await getReservationConfirmation(auth!.token, reservation!.restaurant.id, {
      restaurant_id: reservation!.restaurant.id,
      seating_id: reservation!.seating_id,
      party: reservation!.party_size,
      date: dayjs(reservation!.reservation_at_tzd).format('YYYY-MM-DD'),
      time: reservation!.reservation_time_pretty,
      use_credits: useCreditsValue,
      skip_level_check: true,
    });

    if (response) {
      setReservation((prev) => ({
        ...prev!,
        total_paid: response.reservation.total,
        total_paid__currency: response.reservation.total__currency,
        credits_used: response.reservation.credits_used,
        credits_used__currency: response.reservation.credits_used__currency,
        points_used: response.reservation.points_used,
        points_used_currency_value__currency:
          response.reservation.points_used_currency_value__currency,
      }));
    }
  };

  const requiresPayment = !!reservation && reservation.total_paid > 0;
  const hasValidPaymentMethod = !!directLink.claim_body?.payment_method_id;

  const onNext = () => {
    // Drop double-clicks landing in the same tick. ref flips synchronously,
    // before React commits the disabled-button re-render.
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;

    if (requiresPayment && !hasValidPaymentMethod) {
      // Payment is required but no payment method has been saved —
      // redirect back to the payment step instead of silently failing.
      inFlightRef.current = false;
      navigate(`../payment/`);
      return;
    }

    const go = async () => {
      setBooking(true);

      // A stale error from a previous attempt would mislead if this one fails differently
      updateDirectLink({ payment_error: undefined });

      if (requiresPayment && !hasValidPaymentMethod) {
        // Double-check: payment required but no method saved
        navigate(`../payment/`);
        return;
      }

      if (reservation!.sold_out) {
        navigate(`../unavailable/`);
        return;
      }

      if (reservation!.cancelled) {
        navigate(`../expired/`);
        return;
      }

      try {
        const claimBodyWithCredits = {
          ...directLink.claim_body!,
          use_credits: useCredits,
        };

        const response = auth?.token
          ? await claimReservation(
              auth.token,
              reservation!.restaurant.id,
              directLink.uuid!,
              claimBodyWithCredits
            )
          : await claimReservationForUnclaimedReservation(
              reservation!.restaurant.id,
              directLink.uuid!,
              claimBodyWithCredits
            );

        if (response.reservation) {
          navigate(`../confirmation/`);
        }
      } catch (e: unknown) {
        if (!(e instanceof ApiError)) {
          // Unrecognized failure — still show the guest something actionable [HOS-2029]
          console.error(e);
          Sentry.captureException(e instanceof Error ? e : new Error(String(e)));
          updateDirectLink({ payment_error: GENERIC_CLAIM_ERROR });
          return;
        }

        switch (e.type) {
          case 'ReserveErrorResponse':
          case 'CardErrorResponse':
            updateDirectLink({
              payment_error: e.message,
            });

            navigate(`../payment/`);
            break;

          case 'ReservationUnavailableErrorResponse':
          case 'ReservationResourceNotFound':
          case 'AlreadyCanceledReservationErrorResponse':
            navigate(`../expired/`);
            break;

          case 'ProcessingPaymentErrorResponse':
            navigate(`../processing_payment/`);
            break;

          case 'ActionRequiredResponse': {
            const actionRequired = e.response as ActionRequiredResponse;
            if (!actionRequired.payment_intent_client_secret) {
              updateDirectLink({
                payment_error: 'Additional payment verification is required. Please try again.',
              });
              setBooking(false);
              return;
            }

            // The PaymentIntent is created on the reservation's regional Stripe
            // account (e.g. the EU account for a Saint-Tropez booking), so 3DS
            // must be confirmed with that account's publishable key. The
            // page-level `stripe` instance is loaded with the default platform
            // key (Config.data.stripe.key); using it to confirm a regional PI
            // returns "No such payment_intent". Prefer the regional key the API
            // returns, falling back to the ambient instance only if it's absent.
            const confirmationStripe = actionRequired.stripe_public_key
              ? await loadStripe(actionRequired.stripe_public_key)
              : stripe;

            // oxlint-disable-next-line @typescript-eslint/no-unsafe-argument
            const result = await confirmationStripe?.confirmCardPayment(
              actionRequired.payment_intent_client_secret
            );

            if (!confirmationStripe || !result) {
              // Stripe.js failed to load (e.g. invalid/missing regional key) or
              // the confirmation call never ran, so the PaymentIntent is still
              // unconfirmed. Don't treat that as a successful booking.
              updateDirectLink({
                payment_error: 'Additional payment verification is required. Please try again.',
              });

              Sentry.captureException(
                new Error('3DS confirmation could not run: Stripe.js unavailable')
              );
            } else if (result.error) {
              updateDirectLink({
                payment_error: result.error.message,
              });

              console.error(result.error.message);
              Sentry.captureException(result.error);
            } else {
              navigate(`../confirmation/`);
            }
            break;
          }

          default:
            // Deliberately generic: shapes reaching here carry messages that
            // aren't guest-facing claim copy (e.g. "Server Error")
            console.error(e);
            Sentry.captureException(e);
            updateDirectLink({ payment_error: GENERIC_CLAIM_ERROR });
        }
      } finally {
        setBooking(false);
        inFlightRef.current = false;
      }
    };
    if (!directLink.policy_accepted) {
      updateDirectLink({
        policy_accepted: false,
      });
      setBooking(false);
      inFlightRef.current = false;
    } else {
      void go();
    }
  };
  const onAccept = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.checked) {
      updateDirectLink({
        policy_accepted: true,
      });
    } else {
      updateDirectLink({
        ...directLink,
        policy_accepted: null,
      });
    }
  };

  const onMarketing = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.checked) {
      updateDirectLink({
        ...directLink,
        opt_in_accepted: 1,
      });
    } else {
      updateDirectLink({
        ...directLink,
        opt_in_accepted: 0,
      });
    }
  };

  const payment_method = methods_by_id[directLink.payment_method!];

  if (!reservation) {
    return <Header title="Sorry, no reservation" step={4} show_image={false} />;
  }

  function parseContent(content: string) {
    const split = content.split('\n');

    return (
      <>
        {split.map((line: string, i: number) => (
          <div className={styles.policycontent} key={i}>
            {line}
          </div>
        ))}
      </>
    );
  }

  return (
    <>
      <Header
        title="Confirm your booking"
        step={4}
        show_image={false}
        isEvent={reservation.restaurant.type === 'event'}
      />

      {directLink.policy_accepted === false && (
        <div className={clsx('content', styles.header_error)}>You are missing required fields.</div>
      )}

      <div className="content">
        {reservation && <h2>{reservation.restaurant?.name}</h2>}

        {reservation.restaurant.show_merchandising_price_totals && (
          <>
            {(reservation.restaurant.show_merchandising_tax_and_tip ||
              reservation.credits_used__currency ||
              reservation.points_used_currency_value__currency) && <hr />}
            {reservation.restaurant.show_merchandising_tax_and_tip ? (
              <>
                <div className={styles.payment}>
                  {reservation.table_type && <div>{reservation.table_type}</div>}
                  {!reservation.table_type &&
                    (reservation.restaurant.has_frictionless ||
                      reservation.restaurant.has_dorsia_pay_lite ||
                      reservation.restaurant.type === 'event') && <div>Subtotal</div>}
                  {!reservation.table_type &&
                    !reservation.restaurant.has_frictionless &&
                    !reservation.restaurant.has_dorsia_pay_lite &&
                    reservation.restaurant.type !== 'event' && <div>Prepaid Deposit</div>}
                  <div className={styles.paymentnumber}>
                    {reservation.total_prepayment_amount__currency}
                  </div>
                </div>
                {reservation.tax_rate > 0 && (
                  <div className={styles.payment}>
                    <div>Tax ({reservation.tax_rate}%)</div>
                    <div className={styles.paymentnumber}>{reservation.tax_amount__currency}</div>
                  </div>
                )}
                {reservation.service_charges__currency?.map((service, index) => (
                  <div key={index} className={styles.payment}>
                    <div>{service.label}</div>
                    <div className={styles.paymentnumber}>{service.amount}</div>
                  </div>
                ))}
              </>
            ) : null}

            {reservation.credits_used__currency && (
              <div className={styles.payment}>
                <div>Credits</div>
                <div className={styles.paymentnumber}>{reservation.credits_used__currency}</div>
              </div>
            )}

            {reservation.points_used_currency_value__currency && (
              <div className={styles.payment}>
                <div>Fun Coupons</div>
                <div className={styles.paymentnumber}>
                  {reservation.points_used_currency_value__currency}
                </div>
              </div>
            )}
          </>
        )}

        <hr />
        <div className={clsx('section', styles.payment_methods)}>
          <div>
            <h3>Total</h3>
            {reservation.total_paid > 0 && <div className={styles.method}>Payment Method</div>}
          </div>
          <div>
            <h3 className={styles.amount}>{reservation.total_paid__currency}</h3>
            {reservation.total_paid > 0 && (
              <div className={styles.card_info}>
                {payment_method ? (
                  <>
                    {payment_method.number}
                    <Card name={payment_method.brand as CardNames} />
                  </>
                ) : (
                  <div>Error</div>
                )}
              </div>
            )}
          </div>
        </div>

        <hr />

        {creditsAvailable && (
          <div className={styles.checkboxwrapper}>
            <input
              id="wallet"
              type="checkbox"
              onChange={(e) => {
                void onConfirm(e.target.checked);
              }}
              checked={useCredits}
            />
            <div>
              <label htmlFor="wallet">Use Dorsia Wallet</label>
            </div>
          </div>
        )}

        <div className={clsx('section', styles.policy)}>
          {reservation.restaurant.merchandising_policy_title ? (
            <h3>{reservation.restaurant.merchandising_policy_title}</h3>
          ) : (
            <h3>Cancellation and modification policy</h3>
          )}

          <div className={styles.policywrapper}>
            {reservation.restaurant.merchandising_policy ? (
              <>{parseContent(reservation.restaurant.merchandising_policy)}</>
            ) : reservation.cancellation_policy ? (
              <p>{reservation.cancellation_policy}</p>
            ) : null}
          </div>

          {reservation.restaurant.merchandising_marketing_opt_in_label ? (
            <div className={styles.checkboxwrapper}>
              <div className={styles.checkmarksection}>
                <input id="marketing" type="checkbox" onChange={onMarketing} />
                <span className={styles.checkmark}></span>
              </div>
              <div>
                <label htmlFor="marketing">
                  {reservation.restaurant.merchandising_marketing_opt_in_label}
                </label>
              </div>
            </div>
          ) : null}

          <div className={styles.checkboxwrapper}>
            <div className={styles.checkmarksection}>
              <input id="understood" type="checkbox" onChange={onAccept} />
              <span className={styles.checkmark}></span>
            </div>
            {reservation.restaurant.merchandising_policy_opt_in_label ? (
              <div>
                <label htmlFor="understood">
                  {reservation.restaurant.merchandising_policy_opt_in_label}
                </label>
              </div>
            ) : (
              <div>
                <label htmlFor="understood">
                  I understand and accept Dorsia's cancellation and modification policy and that my
                  use of this service is subject to Dorsia's{' '}
                  <a href="https://api.dorsia.com/terms" target="_blank">
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a href="https://api.dorsia.com/privacy" target="_blank">
                    Privacy Policy
                  </a>
                  .
                </label>
              </div>
            )}
          </div>

          {directLink.policy_accepted === false && (
            <div className={styles.error}>
              You must confirm that you understand Dorsia's{` `}
              {reservation.restaurant.merchandising_policy_title ? (
                <>{reservation.restaurant.merchandising_policy_title}</>
              ) : (
                <>cancellation and modification policy</>
              )}
            </div>
          )}

          {/* Always-mounted live region so the alert announces even when the
              error is present on the route's first render [HOS-2029]. The
              message is a complete sentence — prefixing it doubled it. */}
          <div
            role="alert"
            className={directLink?.payment_error ? styles.error : undefined}
          >
            {directLink?.payment_error}
          </div>

          {requiresPayment && !hasValidPaymentMethod && (
            <div className={styles.error}>
              A payment method is required to complete your booking.{' '}
              <Link to={'../payment/'}>Add a payment method</Link>
            </div>
          )}
        </div>
        <div className="section">
          <button
            className="nextButton"
            onClick={onNext}
            disabled={booking || (requiresPayment && !hasValidPaymentMethod)}
          >
            {booking ? 'Booking...' : 'Complete Booking'}
          </button>
        </div>
        <div className={styles.grid}>
          <div className="section">
            <h3>Reservation details</h3>

            <div>{reservation.reservation_at_tzd.format('ddd, MMMM D')}</div>
            <div>{reservation.reservation_time_pretty}</div>
            <div>{reservation.party_size} guests</div>
            <div>{reservation.table_type}</div>

            {reservation.restaurant.type !== 'event' ? (
              <>
                <div className={styles.minimum}>
                  {reservation.table_minimum__currency} total minimum
                </div>
                <div className={styles.minimum_pp}>
                  {reservation.price_per_person__currency} minimum per person
                </div>
              </>
            ) : (
              <div className="section">
                <h3>Your details</h3>

                <div>
                  {reservation.user.first_name} {reservation.user.last_name}
                </div>
                <div>{reservation.user.email}</div>

                <div className={styles.edit}>
                  <Link to={'../info'} className="lightButton">
                    Edit your details
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
        <RemainingTime />
      </div>
    </>
  );
};

export default Confirm;
